import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { toast } from "react-toastify";
import { getApiData } from "../../utils/APIHelper";
import { Setting } from "../../utils/Setting";
import CInput from "../../components/CInput";
import { ReactComponent as Logo } from "../../assets/svg/Logo.svg";
import { useDispatch } from "react-redux";
import authActions from "../../redux/reducers/auth/actions";

const errorObj = {
  passwordErr: false,
  passwordMsg: "",
  newPasswordErr: false,
  newPasswordMsg: "",
};

const SetupAccount = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const invitor = searchParams.get("invitor");
  const role = searchParams.get("role");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setUserData, setToken } = authActions;

  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [errObj, setErrObj] = useState(errorObj);
  const [btnLoad, setBtnLoad] = useState(false);

  const passwordRegex =
    /^(((?=.*[a-z])(?=.*[A-Z]))((?=.*[a-z]))(?=.*[!@#$%+:^=<(){}[\]~>_?`|'";,.&*€₹£])((?=.*[A-Z])))(?=.{8,})/;

  if (!token || !email) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        fontWeight={600}
      >
        Invalid Request
      </Box>
    );
  }

  const validation = () => {
    const error = { ...errObj };
    let valid = true;

    if (!password) {
      valid = false;
      error.passwordErr = true;
      error.passwordMsg = "Please enter new password";
    } else if (!passwordRegex.test(password)) {
      valid = false;
      error.passwordErr = true;
      error.passwordMsg =
        "Password must include more than 8 characters, at least one number, one letter, one capital letter and one symbol";
    } else if (password.length < 8) {
      valid = false;
      error.passwordErr = true;
      error.passwordMsg = "Password length must be of 8-15";
    } else if (password !== newPassword) {
      valid = false;
      error.passwordErr = true;
      error.passwordMsg = "New password and confirm password must be same";
    }

    if (!newPassword) {
      valid = false;
      error.newPasswordErr = true;
      error.newPasswordMsg = "Please enter confirm password";
    } else if (!passwordRegex.test(newPassword)) {
      valid = false;
      error.newPasswordErr = true;
      error.newPasswordMsg =
        "Confirm Password must include more than 8 characters, at least one number, one letter, one capital letter and one symbol";
    } else if (newPassword.length < 8) {
      valid = false;
      error.newPasswordErr = true;
      error.newPasswordMsg = "Confirm password length must be of 8-15";
    } else if (newPassword !== password) {
      valid = false;
      error.newPasswordErr = true;
      error.newPasswordMsg = "New password and confirm password must be same";
    }

    setErrObj(error);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validation()) return;
    setBtnLoad(true);

    try {
      const response = await getApiData(
        Setting.endpoints.accountPassword,
        "POST",
        { email, token, password, role }
      );

      if (response.success) {
        dispatch(setToken(response?.token));
        dispatch(setUserData(response?.data));
        toast.success(response?.message);
        navigate("/user-login");
      } else {
        toast.error(response.message);
      }
    } catch (err) {
      toast.error(err.toString());
    } finally {
      setBtnLoad(false);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ minHeight: "100vh", bgcolor: "#fafafa" }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 420,
          p: 5,
          borderRadius: 3,
          textAlign: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "10px",
            marginBottom: "1vh",
          }}
        >
          <Logo />
          <div
            style={{
              fontSize: "16px",
              marginTop: "1vh",
              fontWeight: "600",
              color: "#0d0d0e",
              fontFamily: "Inter Tight",
            }}
          >
            {invitor
              ? `${decodeURIComponent(invitor)} has invited you to Reno`
              : "You’ve been invited to Reno"}
          </div>
          <div
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: "#0d0d0e",
              fontFamily: "Inter Tight",
              margin: "1vh 0",
            }}
          >
            Set up your account
          </div>
        </div>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            mt: 2,
            textAlign: "left",
            gap: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CInput outline label="Email" value={email} white={false} disabled />

          <CInput
            outline
            label="New Password"
            placeholder="Enter new password"
            value={password}
            passValue={password}
            passwordValidation
            type={showNewPassword ? "text" : "password"}
            onChange={(e) => {
              setPassword(e.target.value);
              setErrObj({
                ...errObj,
                passwordErr: false,
                passwordMsg: "",
              });
            }}
            white={false}
            inputProps={{ maxLength: 15 }}
            error={errObj.passwordErr}
            helpertext={errObj.passwordMsg}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {!showNewPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
          />

          <CInput
            outline
            label="Confirm Password"
            placeholder="Enter confirm password"
            type={showPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setErrObj({
                ...errObj,
                newPasswordErr: false,
                newPasswordMsg: "",
              });
            }}
            white={false}
            error={errObj.newPasswordErr}
            helpertext={errObj.newPasswordMsg}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {!showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={btnLoad}
            sx={{
              mt: 2,
              py: 1.3,
              borderRadius: 2,
              backgroundColor: "#4D6BF6",
              textTransform: "none",
              fontWeight: 500,
              boxShadow: "none",
              fontSize: 16,
              "&:hover": { backgroundColor: "#4D6BF6" },
            }}
          >
            {btnLoad ? (
              <CircularProgress size={24} sx={{ color: "#fff" }} />
            ) : (
              "Finish Account Setup"
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default SetupAccount;
